import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import UnifiedMenu from '../components/UnifiedMenu';
import { Button } from '../components/ui/button';
import { Calendar, Filter, BookOpen, Clock, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { HOFFMAN_CATEGORIES, getCategoryNames } from '../hoffmanModel';

export default function Journal({ user, onLogout }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeeling, setSelectedFeeling] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchEntries();
    fetchStats();
  }, [selectedFeeling, selectedCategory, startDate, endDate]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedFeeling) params.specific_feeling = selectedFeeling;
      if (selectedCategory) params.primary_category = selectedCategory;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await axios.get(`${API}/journal/entries`, { params });
      setEntries(res.data);
    } catch (error) {
      console.error('Failed to fetch journal entries', error);
      toast.error('Could not load journal entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/journal/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats', error);
    }
  };

  const findLastSimilar = async (feeling) => {
    try {
      const res = await axios.get(`${API}/journal/last-similar`, {
        params: { feeling }
      });
      
      if (res.data.message) {
        toast.info(`This is the first time you've logged "${feeling}"`);
      } else {
        const lastTime = new Date(res.data.timestamp).toLocaleString();
        const reason = res.data.reason || 'No reason noted';
        toast.success(
          `Last time you felt "${feeling}": ${lastTime}`,
          { description: `Reason: ${reason}`, duration: 6000 }
        );
      }
    } catch (error) {
      toast.error('Could not find similar feeling');
    }
  };

  const clearFilters = () => {
    setSelectedFeeling('');
    setSelectedCategory('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="h-screen flex flex-col pb-20 bg-gradient-to-b from-white via-stone-50 to-stone-100">
      <div className="container mx-auto px-4 md:px-6 py-4 md:py-6 max-w-4xl">
        <div className="mb-4">
          <h1 className="text-2xl md:text-3xl font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>
            Your Journal
          </h1>
        </div>

        {/* Compact content - fits on one screen */}
        <div className="space-y-4 max-h-[calc(100vh-180px)] overflow-y-auto">

        {/* Filters */}
        <div className="zen-card p-6 mb-3">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-hunter-green" />
            <h3 className="font-semibold text-lg">Filter Journal Entries</h3>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4">
            {/* Category Filter */}
            <div>
              <label className="text-xs font-medium text-warm-charcoal/70 uppercase tracking-wider mb-2 block">
                Emotion Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm focus:border-hunter-green/50"
              >
                <option value="">All Categories</option>
                {getCategoryNames().map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="text-xs font-medium text-warm-charcoal/70 uppercase tracking-wider mb-2 block">
                From Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm focus:border-hunter-green/50"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="text-xs font-medium text-warm-charcoal/70 uppercase tracking-wider mb-2 block">
                To Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm focus:border-hunter-green/50"
              />
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <Button
                onClick={clearFilters}
                className="w-full zen-button bg-stone-200 text-warm-charcoal hover:bg-stone-300"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        {stats && stats.most_journaled_feelings && stats.most_journaled_feelings.length > 0 && (
          <div className="zen-card p-6 mb-3">
            <h3 className="font-semibold text-lg mb-4">Most Journaled Feelings</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {stats.most_journaled_feelings.slice(0, 5).map((item) => (
                <button
                  key={item.feeling}
                  onClick={() => setSelectedFeeling(item.feeling)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    selectedFeeling === item.feeling
                      ? 'border-hunter-green bg-hunter-green/10'
                      : 'border-stone-200 hover:border-hunter-green/50'
                  }`}
                >
                  <p className="font-semibold text-sm">{item.feeling}</p>
                  <p className="text-xs text-warm-charcoal/50 mt-1">{item.count} entries</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-3">
            <p className="text-warm-charcoal/60">Loading your journal...</p>
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="zen-card p-12 text-center">
            <BookOpen className="w-16 h-16 mx-auto text-warm-charcoal/30 mb-4" />
            <p className="text-warm-charcoal/60 mb-2">No journal entries found</p>
            <p className="text-sm text-warm-charcoal/40">Start tracking emotions with reflections on the dashboard</p>
          </div>
        )}

        {!loading && entries.length > 0 && (
          <div className="space-y-4">
            {entries.map((entry) => {
              const categoryData = HOFFMAN_CATEGORIES[entry.primary_category];
              return (
                <div key={entry.id} className="zen-card p-6 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      <div
                        className="w-12 h-12 rounded-full flex-shrink-0"
                        style={{ backgroundColor: categoryData?.color || '#78716C' }}
                      />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-xl">{entry.specific_feeling}</h3>
                          <button
                            onClick={() => findLastSimilar(entry.specific_feeling)}
                            className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                            title="When did I last feel this?"
                          >
                            <Clock className="w-3 h-3 inline mr-1" />
                            Last time?
                          </button>
                        </div>
                        <p className="text-sm text-warm-charcoal/60">{entry.primary_category}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-warm-charcoal/50">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(entry.timestamp).toLocaleString()}
                          </span>
                          <span>Intensity: {entry.emotional_intensity}/10</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {entry.reason && (
                    <div className="p-4 rounded-lg bg-sage/10 border border-sage/20">
                      <p className="text-sm font-medium text-warm-charcoal/70 mb-1">Reflection:</p>
                      <p className="text-base text-warm-charcoal/80 italic leading-relaxed">
                        "{entry.reason}"
                      </p>
                    </div>
                  )}

                  {entry.body_sensations && entry.body_sensations.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="text-xs text-warm-charcoal/50">Body:</span>
                      {entry.body_sensations.map((s, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                          {s.sensation} ({s.intensity}/10)
                        </span>
                      ))}
                    </div>
                  )}

                  {entry.tags && entry.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {entry.tags.map((tag, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 rounded-full bg-stone-200 text-warm-charcoal">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>

      <UnifiedMenu active="journal" stats={null} currentQuote="" recentMoods={[]} onLogout={onLogout} />
    </div>
  );
}
